import dayjs from 'dayjs';

import { useState } from 'react';
import { useSelector } from 'react-redux';

import Icon from 'components/global/Icon';
import LordIcon from 'components/global/LordIcon';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { THEME } from 'utils/Theme';

import { faAngleDown } from '@fortawesome/pro-solid-svg-icons';

import { FlatpickrWrapper } from './DateInput';
import * as S from './styled/DateFilter.styled';

const dayOffsets = {
	today: 1,
	last7Days: 7,
	last14Days: 14,
	last30Days: 30
};

const actions = [
	{
		name: 'today'
	},
	{
		name: 'last7Days'
	},
	{
		name: 'last14Days'
	},
	{
		name: 'last30Days'
	},
	{
		name: 'customDateRange'
	}
];

/**
 * @author Burak Unsal
 * @description Date filter component
 * @param {object} props
 * @param {"today" | "last7Days" | "last14Days" | "last30Days"} [props.defaultDay="today"]
 * @param {Function} props.setValue
 * @example
 * <DateFilter defaultDay="today" setValue={setFilter} />
 */
const DateFilter = ({ defaultDay = 'today', setValue, ...rest }) => {
	const [localValue, setLocalValue] = useState();
	const [selectedOption, setSelectedOption] = useState(defaultDay);
	const [showDate, setShowDate] = useState(false);
	const [buttonRef, setButtonRef] = useState(null);
	const { getLocaleDate } = useLocale();
	const darkMode = useSelector((state) => state.layout.darkMode);

	useUpdateEffect(() => {
		const isCustomDateRange = selectedOption === 'customDateRange';
		setShowDate(isCustomDateRange);
		if (isCustomDateRange) return;

		setValue({
			start: dayjs().tz().subtract(dayOffsets[selectedOption], 'day').endOf('day').toISOString(),
			end: dayjs().tz().endOf('day').toISOString()
		});
	}, [selectedOption]);

	useUpdateEffect(() => {
		if (localValue?.end !== undefined && selectedOption === 'customDateRange') {
			setValue(localValue);
		}
	}, [localValue]);

	return (
		<S.Container>
			<S.Button
				ref={setButtonRef}
				onClick={(e) => {
					e.preventDefault();
				}}
				{...rest}>
				<LordIcon
					src="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/calendar-icon.json.gz"
					primaryColor={darkMode ? THEME.WHITE : THEME.BLACK}
					secondaryColor={darkMode ? THEME.DARK.PRIMARY_COLOR : THEME.LIGHT.PRIMARY_COLOR}
					width={24}
					height={24}
				/>
				<Typography>
					{localValue
						? `${getLocaleDate(localValue.start)} - ${getLocaleDate(localValue.end)}`
						: actions.find((action) => action.name === selectedOption)?.name}
				</Typography>
				<Icon icon={faAngleDown} className="icon" />
			</S.Button>
			{buttonRef && (
				<Popper refElement={buttonRef} placement="bottom-end">
					{(closePopper) => (
						<S.Content>
							{actions.map((action, index) => (
								<S.ContentRow
									key={index}
									onMouseDown={(event) => event.stopPropagation()}
									onTouchEnd={(event) => event.stopPropagation()}
									onClick={() => {
										setSelectedOption(action.name);
										setLocalValue(undefined);
										action.name !== 'customDateRange' && closePopper();
									}}
									className={action.name === selectedOption ? 'selected' : ''}>
									<Typography>{action.name}</Typography>
								</S.ContentRow>
							))}
							{showDate && (
								<>
									<S.DateInput>
										<FlatpickrWrapper
											value={localValue}
											setValue={(value) => {
												setLocalValue(value);
												if (value.end !== undefined) {
													closePopper();
												}
											}}
											rangeMode
											inline
										/>
									</S.DateInput>
								</>
							)}
						</S.Content>
					)}
				</Popper>
			)}
		</S.Container>
	);
};

export default DateFilter;
